/**
 * "Today's Drill" daily-challenge client + types.
 *
 * Mirrors `backend/app/models.py:DailyChallengeResponse`. One shared
 * target per local date (same for everyone), climbed across three
 * difficulties; finishing all three advances the streak.
 *
 * The client sends its LOCAL date (YYYY-MM-DD) so the day boundary is
 * local, not UTC — a daily reset at UTC midnight would land mid-evening
 * in the Americas.
 */

import { backendUrl } from "@/shared/config";
import { loadTokens } from "@/features/auth/lib/tokenStorage";

export type DailyDifficultyLevel = "foundational" | "default" | "edge_case";

/** Human labels for the ladder rungs. ("default" reads better as
 *  "Standard" in the UI.) */
export const DIFFICULTY_LABEL: Record<DailyDifficultyLevel, string> = {
  foundational: "Foundational",
  default: "Standard",
  edge_case: "Edge case",
};

export interface DailyDifficultyState {
  level: DailyDifficultyLevel;
  done: boolean;
}

export interface DailyChallenge {
  date: string;
  drillTarget: string;
  skillName: string;
  mode: string;
  difficulties: DailyDifficultyState[];
  completedToday: boolean;
  currentStreak: number;
  longestStreak: number;
}

interface RawDailyChallenge {
  date: string;
  drill_target: string;
  skill_name: string;
  mode: string;
  difficulties: { level: DailyDifficultyLevel; done: boolean }[];
  completed_today: boolean;
  current_streak: number;
  longest_streak: number;
}

/** Convert the backend wire shape into the camelCase TS shape.
 *  Exported for the test file. */
export function mapDailyChallenge(raw: RawDailyChallenge): DailyChallenge {
  return {
    date: raw.date,
    drillTarget: raw.drill_target,
    skillName: raw.skill_name,
    mode: raw.mode,
    difficulties: raw.difficulties.map((d) => ({
      level: d.level,
      done: d.done,
    })),
    completedToday: raw.completed_today,
    currentStreak: raw.current_streak,
    longestStreak: raw.longest_streak,
  };
}

/** Today's date in the user's LOCAL timezone as "YYYY-MM-DD". Exported
 *  for testing + reuse by the launch URL (the daily_challenge_date param
 *  must match what we query with). */
export function localDateString(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** The first difficulty the user hasn't finished yet, or null if the
 *  ladder is complete. Drives the card's primary CTA. */
export function nextIncompleteDifficulty(
  dc: DailyChallenge,
): DailyDifficultyLevel | null {
  const next = dc.difficulties.find((d) => !d.done);
  return next ? next.level : null;
}

async function authedRequest<T>(path: string): Promise<T> {
  const tokens = loadTokens();
  if (!tokens) throw new Error("Not signed in");
  const res = await fetch(backendUrl(path), {
    headers: { Authorization: `Bearer ${tokens.accessToken}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Daily-challenge request failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<T>;
}

/** Fetch Today's Drill state for the user's current local date. */
export async function getDailyChallenge(): Promise<DailyChallenge> {
  const date = localDateString();
  const raw = await authedRequest<RawDailyChallenge>(
    `/daily-challenge?date=${encodeURIComponent(date)}`,
  );
  return mapDailyChallenge(raw);
}
